interface EventApplicationStartData {
    event: string;
    ApplicationID: any;
    Product: string;
    ProductVariant: string;
    StepName: any;
    StepNumber: string;
    Market: string;
    BusinessVertical: string;
}

interface EventApplicationProgressData {
    event: string;
    ApplicationID: any;
    Product: string;
    ProductVariant: string;
    StepName: any;
    StepNumber: string;
    Market: string;
    BusinessVertical: string;
}

interface EventApplicationStatusData {
    event: string;
    ApplicationID: any;
    Product: string;
    ProductVariant: string;
    StepName: any;
    StepNumber: string;
    Market: string;
    BusinessVertical: string;
    Status: string;
}

interface EventApplicationSubmittedData {
    event: string;
    ApplicationID: any;
    Product: string;
    ProductVariant: string;
    Market: string;
    BusinessVertical: string;
    ApplicantType: string;
}

type DataLayerEvent =
    | EventApplicationStartData
    | EventApplicationProgressData
    | EventApplicationStatusData
    | EventApplicationSubmittedData;

declare global {
    interface Window {
        dataLayer: any[];
    }
}

// Fallback timeout for environments without GTM (local dev, tests)
const GTM_READY_FALLBACK_MS = 3000;

let isGtmReady = false;
const pendingEvents: DataLayerEvent[] = [];

function setGtmReady(): void {
    if (isGtmReady) return;
    isGtmReady = true;
    flushPendingEvents();
}

function flushPendingEvents(): void {
    if (typeof window === 'undefined') return;

    window.dataLayer = window.dataLayer || [];

    while (pendingEvents.length > 0) {
        const event = pendingEvents.shift();
        if (event) {
            window.dataLayer.push(event);
        }
    }
}

function initGtmReadyListener(): void {
    if (typeof window === 'undefined') return;

    window.dataLayer = window.dataLayer || [];
    const dl = window.dataLayer;

    // Check if gtm_ready was already pushed before this script loaded
    for (const item of dl) {
        if (item?.event === 'gtm_ready') {
            setGtmReady();
            return;
        }
    }

    // Intercept future dataLayer.push calls to detect gtm_ready
    const originalPush = dl.push.bind(dl);
    dl.push = (...args: any[]): number => {
        const result = originalPush(...args);

        for (const arg of args) {
            if (arg?.event === 'gtm_ready') {
                setGtmReady();
            }
        }

        return result;
    };

    // Fallback: If gtm_ready never fires (local dev without GTM/OneTrust),
    // flush pending events after timeout to ensure they're not lost
    setTimeout(() => {
        if (!isGtmReady) {
            console.debug('[GTM] gtm_ready not received, using fallback');
            setGtmReady();
        }
    }, GTM_READY_FALLBACK_MS);
}

function pushToDataLayer(data: DataLayerEvent): void {
    if (typeof window === 'undefined') return;

    if (!isGtmReady) {
        pendingEvents.push(data);
        return;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(data);
}

// Initialize the listener
initGtmReadyListener();

export function EventApplicationStart(applicationId: any, currentPage: any, countryCode: string): void {
    const data: EventApplicationStartData = {
        event: 'ApplicationStart',
        ApplicationID: applicationId,
        Product: 'LOANS',
        ProductVariant: 'Private loan',
        StepName: currentPage,
        StepNumber: 'Not defined',
        Market: countryCode,
        BusinessVertical: 'B2C',
    };

    pushToDataLayer(data);
}

export function EventApplicationProgress(applicationId: any, currentPage: any, countryCode: string): void {
    const data: EventApplicationProgressData = {
        event: 'ApplicationProgress',
        ApplicationID: applicationId,
        Product: 'LOANS',
        ProductVariant: 'Private loan',
        StepName: currentPage,
        StepNumber: 'Not defined',
        Market: countryCode,
        BusinessVertical: 'B2C',
    };

    pushToDataLayer(data);
}

export function EventApplicationStatus(
    applicationId: any,
    currentPage: any,
    countryCode: string,
    applicationStatus: string,
): void {
    const data: EventApplicationStatusData = {
        event: 'ApplicationStatus',
        ApplicationID: applicationId,
        Product: 'LOANS',
        ProductVariant: 'Private loan',
        StepName: currentPage,
        StepNumber: 'Not defined',
        Market: countryCode,
        BusinessVertical: 'B2C',
        Status: applicationStatus,
    };

    pushToDataLayer(data);
}

export function EventApplicationSubmitted(applicationId: any, countryCode: string, applicantType: string): void {
    const data: EventApplicationSubmittedData = {
        event: 'ApplicationSubmitted',
        ApplicationID: applicationId,
        Product: 'LOANS',
        ProductVariant: 'Private loan',
        Market: countryCode,
        BusinessVertical: 'B2C',
        ApplicantType: applicantType,
    };

    pushToDataLayer(data);
}
